import {IMachinery, INewMachinery, IThresholdDefaults} from "../../../models/iMachinery";
import {appAPI} from "../../../api";

const machineryPath = `/machinery`;
const machineryCurrentLocationPath = `/machinery-current-location`;
const inputPresetPath = `/machinery-input-preset`;

export const machineryAPI = {
    /**
     * @param companyId  Компания клиента — только для админа платформы,
     *                   который заводит технику из «Техники клиентов».
     *                   У обычного пользователя бэкенд поле игнорирует.
     */
    add: async (machinery: INewMachinery, companyId?: string) => {
        try {
            const res = await appAPI.post(machineryPath, {
                ...machinery,
                operating: Number(machinery.operating),
                odometer: Number(machinery.odometer),
                ...(companyId ? {company_id: companyId} : {}),
            });
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    getAll: async () => {
        const res = await appAPI.get(machineryPath);
        return res.data;
    },

    getOptions: async () => {
        const res = await appAPI.get(`${machineryPath}/options`);
        return res.data;
    },

    getById: async (machinery_id: string) => {
        const res = await appAPI.get(`${machineryPath}/${machinery_id}`);
        return res.data;
    },

    /**
     * Пороги по умолчанию для машины. Ключи ответа совпадают с именами
     * колонок машины, поэтому карточке не нужен словарь соответствий.
     */
    getThresholdDefaults: async (machinery_id: string): Promise<IThresholdDefaults> => {
        const res = await appAPI.get(`${machineryPath}/${machinery_id}/threshold-defaults`);
        return res.data;
    },

    getByCompany: async (companyId: string) => {
        const res = await appAPI.get(`${machineryCurrentLocationPath}/by-company/${companyId}`);
        return res.data;
    },

    // Узкое обновление ТОЛЬКО вида топлива (без полного объекта машины).
    // Полный update() принудительно шлёт operating/odometer через Number(),
    // поэтому для частичной правки нужен отдельный путь: PATCH /machinery/{id} { fuel_type }.
    updateFuelType: async (id: string, fuel_type: string): Promise<IMachinery> => {
        try {
            const res = await appAPI.patch(`${machineryPath}/${id}`, {fuel_type});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    update: async (machinery: IMachinery) => {
        try {
            // Потолок ДУТ приходит либо числом, либо незаполненным: null из API
            // и пустой строкой из формы (useEditor кладёт e.target.value как есть).
            // Number(null) и Number("") дают 0, а 0 в БД читается как "потолок
            // задан" — незаполненное поле обязано уехать null'ом.
            const ceiling = machinery.fuel_sensor_ceiling_liters as number | string | null | undefined;
            const res = await appAPI.put(`${machineryPath}/${machinery.id}/`, {
                ...machinery,
                operating: Number(machinery.operating),
                odometer: Number(machinery.odometer),
                fuel_sensor_ceiling_liters:
                    ceiling === null || ceiling === undefined || ceiling === "" ? null : Number(ceiling),
            });
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    // Узкое обновление dispatch-параметров машины (PATCH), по образцу
    // updateTankCapacities: грузоподъёмность/объём/высота/способы загрузки/
    // погрузчик/стоянка. Пустые строки из формы уезжают null'ами.
    updateDispatchParams: async (
        id: string,
        body: {
            payload_capacity_kg: number | null;
            cargo_volume_m3: number | null;
            height_m: number | null;
            loading_methods: string[] | null;
            forklift_compatible: boolean;
            parking_lat: number | null;
            parking_lon: number | null;
        },
    ): Promise<IMachinery> => {
        try {
            const res = await appAPI.patch(`${machineryPath}/${id}`, body);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    // Узкое обновление объёмов баков (PATCH), по образцу updateFuelType.
    updateTankCapacities: async (
        id: string,
        body: { fuel_tank_capacity_liters: number | null; fuel_tank_capacity_liters_2: number | null },
    ): Promise<IMachinery> => {
        try {
            const res = await appAPI.patch(`${machineryPath}/${id}`, body);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    delete: async (machinery_id: string) => {
        try {
            const res = await appAPI.delete(`${machineryPath}/${machinery_id}`);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    // ─── Блокировка ДЭС ───────────────────────────────────────
    lock: async (id: string, password: string) => {
        try {
            const res = await appAPI.post(`${machineryPath}/${id}/lock`, {password});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    unlock: async (id: string) => {
        try {
            const res = await appAPI.post(`${machineryPath}/${id}/unlock`, {});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    // ─── Управление выходами терминала ────────────────────────
    getOutputs: async (id: string) => {
        const res = await appAPI.get(`${machineryPath}/${id}/outputs`);
        return res.data;
    },
    saveOutputs: async (id: string, outputs: unknown[]) => {
        try {
            const res = await appAPI.put(`${machineryPath}/${id}/outputs`, {outputs});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            // Сообщение бэка может прийти массивом (ошибки class-validator) —
            // показываем как есть, оно адресовано пользователю карточки.
            throw new Error(Array.isArray(message) ? message.join("; ") : message);
        }
    },
    // ─── Настройка входов терминала ───────────────────────────
    getInputs: async (id: string) => {
        const res = await appAPI.get(`${machineryPath}/${id}/inputs`);
        return res.data;
    },
    saveInputs: async (id: string, inputs: unknown[]) => {
        try {
            const res = await appAPI.put(`${machineryPath}/${id}/inputs`, {inputs});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            // Как и у выходов: class-validator отдаёт массив сообщений,
            // а текст адресован пользователю карточки — показываем как есть.
            throw new Error(Array.isArray(message) ? message.join("; ") : message);
        }
    },
    // ─── Шаблоны таблиц преобразования входов ─────────────────
    // Фильтр необязателен: без параметров выдача прежняя — весь список.
    // С company_id бэкенд отдаёт шаблоны этой компании плюс общие.
    getInputPresets: async (query?: {company_id?: string | null; role?: string}) => {
        const params = new URLSearchParams();
        if (query?.company_id) params.set("company_id", query.company_id);
        if (query?.role) params.set("role", query.role);
        const qs = params.toString();
        const res = await appAPI.get(qs ? `${inputPresetPath}?${qs}` : inputPresetPath);
        return res.data;
    },
    createInputPreset: async (body: unknown) => {
        try {
            const res = await appAPI.post(inputPresetPath, body);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            // Как и у входов: class-validator отдаёт массив сообщений,
            // а текст адресован пользователю модалки — показываем как есть.
            throw new Error(Array.isArray(message) ? message.join("; ") : message);
        }
    },
    deleteInputPreset: async (id: string) => {
        try {
            const res = await appAPI.delete(`${inputPresetPath}/${id}`);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(Array.isArray(message) ? message.join("; ") : message);
        }
    },
    runOutputAction: async (
        id: string,
        action: string,
        body: {state?: string; password?: string},
    ) => {
        try {
            const res = await appAPI.post(`${machineryPath}/${id}/outputs/${action}`, body);
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(Array.isArray(message) ? message.join("; ") : message);
        }
    },
    getLockStatus: async (id: string) => {
        const res = await appAPI.get(`${machineryPath}/${id}/lock-status`);
        return res.data;
    },
    // ─── Режим заморозки ──────────────────────────────────────
    freeze: async (id: string) => {
        try {
            const res = await appAPI.post(`${machineryPath}/${id}/freeze`, {});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
    unfreeze: async (id: string) => {
        try {
            const res = await appAPI.post(`${machineryPath}/${id}/unfreeze`, {});
            return res.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
            throw new Error(message);
        }
    },
};
